import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import MathRenderer from '../components/MathRenderer';
import { ArrowLeft, TrendingUp, Target, BookOpen, Award, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const COLORS = ['#FF6B35', '#1B4F8A', '#06D6A0', '#FFD23F', '#8338EC', '#E63946'];

export default function ProgressPage() {
  const { API, getHeaders } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchProgress = useCallback(async () => {
    try {
      const res = await fetch(`${API}/student/progress`, { headers: getHeaders() });
      setData(await res.json());
    } catch { toast.error('Failed to load progress'); }
    finally { setLoading(false); }
  }, [API, getHeaders]);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFF8F0' }}>
      <div className="space-y-3 w-full max-w-md px-4">
        {[1,2,3,4].map(i => <div key={i} className="h-24 bg-[#F0E6D8] rounded-2xl animate-pulse" />)}
      </div>
    </div>
  );

  const streak = data?.streak || {};
  const chapterData = (data?.chapter_progress || []).map(c => ({
    name: c.chapter?.replace(/_/g, ' ').slice(0, 15) || 'Unknown',
    accuracy: c.accuracy,
    attempted: c.attempted,
  }));
  const last7 = (data?.last_7_days || []).map(d => ({
    day: new Date(d.date + 'T00:00:00').toLocaleDateString('en', { weekday: 'short' }),
    score: d.score,
    attempted: d.attempted,
  }));
  const strongAreas = data?.strong_areas || [];
  const weakAreas = data?.weak_areas || [];

  const pieData = [
    { name: 'Correct', value: data?.total_correct || 0 },
    { name: 'Incorrect', value: (data?.total_attempted || 0) - (data?.total_correct || 0) },
  ];

  const tabs = [
    { key: 'overview', label: 'Overview', icon: TrendingUp },
    { key: 'chapters', label: 'Chapters', icon: BookOpen },
    { key: 'weekly', label: 'Weekly', icon: Calendar },
  ];

  return (
    <div className="min-h-screen pb-24" style={{ background: '#FFF8F0' }}>
      <div className="sticky top-0 z-20 bg-white border-b-2 border-[#E5DACE] px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate('/home')} data-testid="progress-back-btn"><ArrowLeft className="w-5 h-5 text-[#1B4F8A]" /></button>
        <h1 className="font-extrabold text-[#1B4F8A] text-lg">My Progress</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-4 mt-3">
        {tabs.map(t => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setActiveTab(t.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold transition-all
              ${activeTab === t.key ? 'bg-[#1B4F8A] text-white' : 'bg-white text-slate-500 border-2 border-[#E5DACE]'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="max-w-2xl mx-auto px-4 mt-4">
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4" data-testid="progress-stats">
              <Card className="border-2 border-[#E5DACE] rounded-2xl"><CardContent className="p-4 text-center">
                <p className="text-3xl font-black text-[#FF6B35]">{streak.current_streak || 0}🔥</p>
                <p className="text-xs font-bold text-slate-500">Current Streak</p>
              </CardContent></Card>
              <Card className="border-2 border-[#E5DACE] rounded-2xl"><CardContent className="p-4 text-center">
                <p className="text-3xl font-black text-[#06D6A0]">{data?.accuracy || 0}%</p>
                <p className="text-xs font-bold text-slate-500">Accuracy</p>
              </CardContent></Card>
              <Card className="border-2 border-[#E5DACE] rounded-2xl"><CardContent className="p-4 text-center">
                <p className="text-3xl font-black text-[#1B4F8A]">{data?.total_attempted || 0}</p>
                <p className="text-xs font-bold text-slate-500">Questions Done</p>
              </CardContent></Card>
              <Card className="border-2 border-[#E5DACE] rounded-2xl"><CardContent className="p-4 text-center">
                <p className="text-3xl font-black text-[#FFD23F]">{streak.longest_streak || 0}</p>
                <p className="text-xs font-bold text-slate-500">Best Streak</p>
              </CardContent></Card>
            </div>

            {/* Accuracy Pie */}
            {data?.total_attempted > 0 && (
              <Card className="border-2 border-[#E5DACE] rounded-2xl mb-4">
                <CardContent className="p-4">
                  <h3 className="font-bold text-[#1B4F8A] text-sm mb-2">Accuracy Breakdown</h3>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={70} dataKey="value" paddingAngle={5}>
                        {pieData.map((_, i) => <Cell key={i} fill={i === 0 ? '#06D6A0' : '#E5DACE'} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center gap-4 text-xs font-bold">
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#06D6A0]" /> Correct: {data?.total_correct}</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#E5DACE]" /> Wrong: {(data?.total_attempted || 0) - (data?.total_correct || 0)}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Strong & Weak Areas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {strongAreas.length > 0 && (
                <Card className="border-2 border-[#06D6A0] rounded-2xl">
                  <CardContent className="p-4">
                    <h3 className="font-bold text-[#06D6A0] text-sm mb-2 flex items-center gap-1"><Award className="w-4 h-4" /> Strong Areas</h3>
                    {strongAreas.map((a, i) => (
                      <div key={i} className="flex justify-between text-xs font-bold py-1">
                        <span className="text-slate-600">{a.chapter?.replace(/_/g, ' ')}</span>
                        <span className="text-[#06D6A0]">{a.accuracy}%</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              {weakAreas.length > 0 && (
                <Card className="border-2 border-red-200 rounded-2xl">
                  <CardContent className="p-4">
                    <h3 className="font-bold text-red-500 text-sm mb-2 flex items-center gap-1"><Target className="w-4 h-4" /> Needs Practice</h3>
                    {weakAreas.map((a, i) => (
                      <div key={i} className="flex justify-between text-xs font-bold py-1">
                        <span className="text-slate-600">{a.chapter?.replace(/_/g, ' ')}</span>
                        <span className="text-red-500">{a.accuracy}%</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}

        {activeTab === 'chapters' && (
          <Card className="border-2 border-[#E5DACE] rounded-2xl">
            <CardContent className="p-4">
              <h3 className="font-bold text-[#1B4F8A] text-sm mb-3">Chapter-wise Accuracy</h3>
              {chapterData.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(200, chapterData.length * 35)}>
                  <BarChart data={chapterData} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5DACE" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                    <Tooltip formatter={v => `${v}%`} />
                    <Bar dataKey="accuracy" radius={[0, 6, 6, 0]}>
                      {chapterData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">Complete some practice sessions to see chapter progress</p>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'weekly' && (
          <Card className="border-2 border-[#E5DACE] rounded-2xl">
            <CardContent className="p-4">
              <h3 className="font-bold text-[#1B4F8A] text-sm mb-3">Weekly Performance</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={last7}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5DACE" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fontWeight: 'bold' }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={v => `${v}%`} />
                  <Line type="monotone" dataKey="score" stroke="#FF6B35" strokeWidth={3} dot={{ fill: '#FF6B35', r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-1">
                {last7.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-xs font-bold">
                    <span className="text-slate-500">{d.day}</span>
                    <div className="flex-1 mx-3 h-2 bg-[#F0E6D8] rounded-full overflow-hidden">
                      <div className="h-full bg-[#FF6B35] rounded-full" style={{ width: `${d.score}%` }} />
                    </div>
                    <span className="text-[#1B4F8A]">{d.score}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <BottomNav active="progress" />
    </div>
  );
}
